import { Link, useParams, useLocation } from "@tanstack/react-router";
import { MoreHorizontal } from "lucide-react";
import type { ComponentType } from "react";
import type { ModuleManifest } from "@praesto/contracts";
import { useCore } from "../lib/core";

/**
 * Bottom-nav fixed: 4 module icons + "Altro" drawer button.
 * Tap module → navigate; "Altro" opens MoreDrawer.
 *
 * Mobile-first: 56px nav height + bottom safe-area inset, each tab a
 * full-height flex-1 column so the 5 tap targets fill the width with a
 * 44px+ hit area.
 */
export function MobileBottomNav({
  manifests,
  onOpenMore,
}: {
  manifests: ReadonlyArray<ModuleManifest>;
  onOpenMore: () => void;
}) {
  const core = useCore();
  const { tenantSlug } = useParams({ strict: false }) as {
    tenantSlug: string;
  };
  const location = useLocation();
  // Show first 4 modules; rest go to More drawer
  const visible = manifests.slice(0, 4);

  return (
    // Always-Night surface (C3F v18): surface-night redeclares the full token
    // family, so muted/active inks below resolve against the dark bar in BOTH
    // themes. Active tab in accent — the Spectrum hairline was retired here:
    // the signature is not a repeated indicator.
    <nav className="surface-night fixed bottom-0 left-0 right-0 z-30 flex h-14 items-stretch border-t border-border-default bg-sidebar-bg pb-safe px-safe">
      {visible.map((m) => (
        <NavTab
          key={m.id}
          icon={m.icon as unknown as ComponentType<{ size?: number }>}
          label={core.i18n.t(`${m.id}.title`)}
          to={`/${tenantSlug}/${m.id}`}
          active={location.pathname.startsWith(`/${tenantSlug}/${m.id}`)}
        />
      ))}
      <button
        onClick={onOpenMore}
        className="tap-target flex-1 flex flex-col items-center justify-center gap-0.5 text-sidebar-muted hover:text-sidebar-fg"
        aria-label={core.i18n.t("shell.nav.more")}
      >
        <MoreHorizontal size={20} />
        <span className="text-nav-label truncate max-w-[64px]">
          {core.i18n.t("shell.nav.more")}
        </span>
      </button>
    </nav>
  );
}

function NavTab({
  icon: Icon,
  label,
  to,
  active,
}: {
  icon: ComponentType<{ size?: number }>;
  label: string;
  to: string;
  active: boolean;
}) {
  return (
    <Link
      to={to as never}
      className={`tap-target flex-1 flex flex-col items-center justify-center gap-0.5 ${
        active ? "text-sidebar-accent" : "text-sidebar-muted hover:text-sidebar-fg"
      }`}
    >
      <Icon size={20} />
      <span className="text-nav-label truncate max-w-[64px]">{label}</span>
    </Link>
  );
}
